import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MyAccountService from "../../services/MyAccountService";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";

import { validateUpdateInput, parseUpdateError } from "../../utils/updateUserErrorHandler";

import "./ProfileModals.css";

const EditUserModal = ({ isOpen, onClose, user, token, onUpdated }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email] = useState(user?.email || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetState = () => {
    setUsername(user?.username || "");
    setName(user?.name || "");
    setSurname(user?.surname || "");
    setPhoneNumber(user?.phone_number || "");
    setOldPassword("");
    setNewPassword("");
    setError("");
    setSaving(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateUpdateInput({
      username,
      oldPassword,
      newPassword,
      name,
      surname,
      phoneNumber,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        email,
        username,
        name,
        surname,
        phone_number: phoneNumber,
        old_password: oldPassword || null,
        new_password: newPassword || null,
      };

      const updated = await MyAccountService.updateUserData(payload);
      setSuccess(t("profile.profileUpdated"));
      onUpdated?.(updated);
    } catch (err) {
      const friendly = parseUpdateError(err);
      setError(friendly);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={t("profile.editProfile")}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? t("profile.saving") : t("profile.saveChanges")}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="profile-modal-form">
        <label>
          {t("profile.username")}
          <MainInput
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          {t("profile.name")}
          <MainInput
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          {t("profile.surname")}
          <MainInput
            type="text"
            value={surname}
            onChange={(e) => {
              setSurname(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          {t("profile.phoneNumber")}
          <MainInput
            type="text"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          {t("profile.currentPassword")}
          <MainInput
            type="password"
            value={oldPassword}
            placeholder={t("profile.passwordKeepHint")}
            onChange={(e) => {
              setOldPassword(e.target.value);
              setError("");
            }}
          />
        </label>

        <label>
          {t("profile.newPassword")}
          <MainInput
            type="password"
            value={newPassword}
            placeholder={t("profile.passwordKeepHint")}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setError("");
            }}
          />
        </label>

        {error && (
          <div className="profile-user-edit-error">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {success && (
          <div className="profile-user-edit-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditUserModal;
